import {
    CloseCircleFilled,
    StarFilled,
    StarOutlined
} from '@ant-design/icons';
import {Button, message, Popconfirm, Spin, Table} from 'antd';
import type {ColumnsType} from 'antd/es/table';
import React, {useEffect, useState} from 'react';
import {useHistory, useRouteMatch} from "react-router-dom";
import {UserDetails} from "../../actions/api";
import {getPermissions, selectUser} from "../../actions/userSlice";
import styles from './QueryHistory.module.scss'
import './DropdownMenuQueryStyles.scss';
import buttonStyles from "../../components/common/styled/Buttons.module.scss";
import {apiFetchQueries, apiUpdateQuery} from '../../containers/QueryBuilder/queryBuilderApi';
import {UserQuery} from '../../containers/QueryBuilder/types/UserQuery';
import {useAppSelector} from "../../hooks";
import {getHistoryRoute, getQueryBuilderCohortAnalyserRoute} from "../../pages/routes";
import EditableLabel from '../EditableLabel';
import {QueryBuilderTemplate} from './QueryBuilderTemplate';
import UsersDialog from "./components/UsersDialog";
import {generateQueryHistoryViewTitle} from "./queryHistoryUtil";
import {QueriesDropDown} from "./QueriesDropDown";
import NewQueryButton from "./NewQueryButton";
import {GenericSearch} from "../AppSearch/GenericSearch";

export const queryDetailHtml = (query: string) => {
    return query
        .replace("Filters", "<strong>Filters</strong>")
        .replace("Values", "<strong>Values</strong>")
        .replace("Rows", "<strong>Rows</strong>")
        .replace("Columns", "<strong>Columns</strong>")
        .replace(",Data Type", "")
        .replace("| Filters : Data Type", "")
        .replaceAll("|", "<br>")
}

interface QueryHistoryProps {
    isCohortAnalyser: boolean
}

export const QueryHistory = ({isCohortAnalyser}: QueryHistoryProps) => {

    const user = useAppSelector(selectUser) as UserDetails
    const match = useRouteMatch<{ filter: string }>(getHistoryRoute(isCohortAnalyser))!
    const [list, setList] = useState<UserQuery[] | undefined>(undefined)
    const [isShowUserDialog, setShowUserDialog] = useState<boolean>(false)
    const users = React.useRef<string | null>(null)
    const query = React.useRef<UserQuery | undefined>(undefined)
    const [searchTerm, setSearchTerm] = useState('')

    useEffect(() => {
        apiFetchQueries(match.params.filter, isCohortAnalyser).then(i => { setList(i) })
    }, [match.params.filter, isCohortAnalyser])

    const columns = (): ColumnsType<UserQuery> => {
        return [
            {
                title: 'Category',
                dataIndex: 'category',
                render: (value, record, index) => {
                    if (record.loginId === user.username) {
                        return <EditableLabel dataTestId={`query-category-${record.queryId}`} text={value ?? ''} onSaveNotification={function (value: string): void {
                            apiUpdateQuery(record.queryId, { category: value })
                                .then(() => {
                                    apiFetchQueries(match.params.filter, isCohortAnalyser)
                                        .then(i => {
                                            setList(i)
                                            message.info(`Category has been updated to ${value}.`);
                                        })
                                })
                        }} />
                    } else {
                        return value
                    }
                },
                sorter: (a, b) => (a.category ?? '').localeCompare(b.category ?? ''),

                filteredValue: [searchTerm],
                onFilter: (input, record) => {
                    let searchInput = input.toString().toLowerCase()
                    let category = record.category?.toString().toLowerCase()
                    let label = record.label?.toString().toLowerCase()
                    let query = record.query.toString().toLowerCase()
                    let createdOn = new Date(record.createdOn).toLocaleDateString('en-GB')
                    let loginId = record.loginId.toString().toLowerCase()
                    return (category?.includes(searchInput) || label?.includes(searchInput) ||
                            query.includes(searchInput) || createdOn.includes(searchInput) ||
                            loginId.includes(searchInput)
                    )
                },

            },
            {
                title: 'Label',
                dataIndex: 'label',
                render: (value, record, index) => {
                    if (record.loginId === user.username) {
                        return <EditableLabel dataTestId={`query-label-${record.queryId}`} text={value ?? ''} onSaveNotification={function (value: string): void {
                            apiUpdateQuery(record.queryId, { label: value })
                                .then(() => {
                                    apiFetchQueries(match.params.filter, isCohortAnalyser)
                                        .then(i => {
                                            setList(i)
                                            message.info(`Label has been updated to ${value}.`);
                                        })
                                })
                        }} />
                    } else {
                        return value
                    }
                },
                sorter: (a, b) => (a.label ?? '').localeCompare(b.label ?? ''),
            },
            {
                title: 'Query Detail',
                dataIndex: 'query',
                render: (value, record, index) => {
                    const html = queryDetailHtml(value)
                    return <span data-testid={`query-summary-${record.queryId}`} dangerouslySetInnerHTML={{ __html: html }}></span>
                }
            },
            {
                title: 'Load Query',
                dataIndex: 'viz',
                render: (value, record, index) => {
                    return <Button
                        className={buttonStyles["loadQuery"]}
                        onClick={() => {
                            history.push(`${getQueryBuilderCohortAnalyserRoute(isCohortAnalyser)}?queryId=${record.queryId}`)
                        }}
                    >
                        Load Query
                    </Button>
                }
            },
            {
                title: 'Date Created',
                dataIndex: 'createdOn',
                render: (value, record, index) => new Date(value).toLocaleDateString('en-GB'),
                sorter: (a, b) => a.createdOn - b.createdOn,
                defaultSortOrder: "descend"
            },
            {
                title: 'Owner',
                dataIndex: 'loginId',
                render: (value) => value,
                sorter: (a, b) => (a.loginId).localeCompare(b.loginId),
            },
            {
                title: 'Shared',
                dataIndex: 'shared',
                render: (value, record, index) => {
                    if (record.loginId === user.username) {
                        const onClick = () => {
                            query.current = record
                            users.current = record.sharedUser
                            setShowUserDialog(true)
                        }
                        if (record.saved) {
                            if (record.sharedUser && record.sharedUser.length > 0) {
                                const count = record.sharedUser.split(' ').length
                                return <Button className={buttonStyles["shareQuery"]} onClick={onClick}>
                                           Shared ({count})
                                       </Button>
                            } else {
                                return <Button className={buttonStyles["shareQuery"]} onClick={onClick}>
                                             Not Shared
                                       </Button>
                            }
                        } else {
                            const onSave = () => {
                                apiUpdateQuery(record.queryId, { saved: true })
                                    .then(() => {
                                        apiFetchQueries(match.params.filter, isCohortAnalyser)
                                            .then(i => { setList(i) })
                                    })
                            }
                            return <Button
                                className={buttonStyles["neutralQuery"]}
                                onClick={onSave}
                            >
                                Save This Query
                            </Button>
                        }
                    } else {
                        return <Button
                            className={buttonStyles["neutralQuery"]}
                            disabled
                        >
                            Not Owner
                        </Button>
                    }
                },
            },
            {
                title: 'Favourite',
                dataIndex: 'favourite',
                render: (value, record, index) => {
                    const onConfirm = (favourite: boolean) => {
                        apiUpdateQuery(record.queryId, {  favourite: favourite })
                            .then(() => {
                                apiFetchQueries(match.params.filter, isCohortAnalyser)
                                    .then(i => { setList(i) })
                            })
                    }
                    if (value && value.includes(user.username)) {
                        return <Popconfirm
                            title={'Do you want to unfavourite this query?'}
                            onConfirm={() => onConfirm(false)}
                            okText="Yes"
                            cancelText="No"
                        >
                            <StarFilled style={{ color: '#FBB03A' }} />
                        </Popconfirm>
                    } else {
                        return <Popconfirm
                            title={'Do you want to favourite this query?'}
                            onConfirm={() => onConfirm(true)}
                            okText="Yes"
                            cancelText="No"
                        >
                            <StarOutlined style={{ color: '#FBB03A' }} />
                        </Popconfirm>
                    }
                }
            },
            {
                title: 'Delete',
                dataIndex: 'delete',
                render: (value, record, index) => {
                    if (record.loginId === user.username) {
                        const onConfirm = () => {
                            apiUpdateQuery(record.queryId, { delete: true })
                                .then(() => {
                                    apiFetchQueries(match.params.filter, isCohortAnalyser)
                                        .then(i => { setList(i) })
                                })
                        }
                        return <Popconfirm
                            title={'Do you want to remove this saved query?'}
                            onConfirm={onConfirm}
                            okText="Yes"
                            cancelText="No"
                        >
                            <CloseCircleFilled style={{ color: '#C44632' }} />
                        </Popconfirm>
                    } else {
                        return null
                    }
                }
            },
        ]
    }

    const history = useHistory()

    const buttons = [
        ...(!isCohortAnalyser ? [
            <NewQueryButton key='1'
                            onClick={() => history.push(getQueryBuilderCohortAnalyserRoute(isCohortAnalyser))}
            />
        ] : []),
        <QueriesDropDown key='2' isCohortAnalyser={isCohortAnalyser}
                         hasCompanyQueryHistoryAccess={useAppSelector(getPermissions).companyQueryHistory}
        />
    ]

    const callback = (usersString: string) => {
        apiUpdateQuery(query.current!.queryId, { shared: true, sharedUser: usersString })
            .then(() => {
                apiFetchQueries(match.params.filter, isCohortAnalyser)
                    .then(i => { setList(i) })
            })
        users.current = null
        query.current = undefined
    }

    return <>
        <QueryBuilderTemplate buttons={buttons} isCohortAnalyser={isCohortAnalyser}>

            <GenericSearch placeholder='Search...'
                           className ={styles.queryHistorySearchBar}
                           allowClear
                           onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchTerm(e.target.value)}
            />

            <div className={styles.queryHistoryViewTitle}>
                {generateQueryHistoryViewTitle(history.location.pathname.split('/').pop())}
            </div>

            {
                list ?
                    <Table className={styles.queryTable}
                           data-testid={"query-table"}
                           rowKey='queryId'
                           columns={columns()}
                           dataSource={list}
                           pagination={{
                               position: ['topRight', 'bottomRight'],
                               showSizeChanger: true
                           }}
                           bordered
                    />
                    : <Spin/>
            }
        </QueryBuilderTemplate>

        {isShowUserDialog ?
            <UsersDialog isOpen={isShowUserDialog}
                         setOpen={setShowUserDialog}
                         usersString={users.current}
                         callback={callback}
            /> : null
        }

    </>

}
